import React, { useEffect, useState } from "react";
import { Dialog } from "@primer/react/drafts";
import {
  Box,
  Text,
  TextInput,
  FormControl,
  Button,
  ButtonGroup,
  ToggleSwitch,
} from "@primer/react";
import { useForm } from "react-hook-form";
import moment from "moment";
import { useCommonContext } from "../../contexts/common";
import { NotifyHelper } from "../../helpers/toast";
import { kyThiApi } from "../../api/kyThiApi";
import { KyThiItemRequest } from "../../models/request/ky-thi/KyThiItemRequest";
import { ComboboxTruong } from "../../components/combobox-truong";
import ComboboxKhoi from "../../components/combobox-khoi";
import ComboboxHe from "../../components/combobox-he";
import { useCommonSelectedHook } from "../../hooks/useCommonSelectedHook";

interface KyThiDetailModalProps {
  id: number;
  onSuccess: () => void;
  onCancel: () => void;
}

interface ComboboxProps {
  value: number;
  onValueChanged: (value: number) => void;
}

interface KhoiComboboxProps extends ComboboxProps {
  dm_truong_id: number;
}

const KyThiDetailModal: React.FC<KyThiDetailModalProps> = ({
  id,
  onSuccess,
  onCancel,
}) => {
  const { translate } = useCommonContext();
  const { nam_hoc, dm_coso_id } = useCommonSelectedHook();
  const [isSaving, setIsSaving] = useState(false);

  const defaultValues: KyThiItemRequest = {
    id: id,
    nam_hoc: nam_hoc,
    ten_ky_thi: "",
    ten_ky_thi_en: "",
    dm_truong_id: 0,
    dm_he_id: 0,
    dm_khoi_id: 0,
    ngay_thi: "",
    da_ket_thuc: false,
    thoi_gian_tu: "",
    thoi_gian_den: "",
    ngay_thi_converted: "",
    thoi_gian_tu_converted: "",
    thoi_gian_den_converted: "",
  };

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    setValue,
    watch,
  } = useForm<KyThiItemRequest>({
    defaultValues,
  });

  const formValues = watch();

  const formatTimeForInput = (timeString: string | null | undefined) => {
    if (!timeString) return "";

    // If timeString is in "DD-MM-YYYY HH:mm" format
    if (timeString.includes("-")) {
      const time = moment(timeString, "DD-MM-YYYY HH:mm");
      if (time.isValid()) {
        return time.format("HH:mm");
      }
    }

    // If timeString is already in "HH:mm" format
    if (/^\d{2}:\d{2}$/.test(timeString)) {
      return timeString;
    }

    // Try parsing as regular time
    const time = moment(timeString, ["HH:mm:ss", "HH:mm"]);
    if (time.isValid()) {
      return time.format("HH:mm");
    }

    return "";
  };

  const formatDateForInput = (dateString: string | null | undefined) => {
    if (!dateString) return "";

    // Try parsing in various formats
    const date = moment(dateString, ["DD-MM-YYYY", "YYYY-MM-DD"]);
    if (date.isValid()) {
      return date.format("YYYY-MM-DD");
    }

    return "";
  };

  const loadDetail = async () => {
    if (id > 0) {
      try {
        const res = await kyThiApi.selectById(id);
        if (res.is_success && res.data) {
          const data = res.data as KyThiItemRequest;
          console.log("API Response:", data);

          const formattedData = {
            ...data,
            ngay_thi: formatDateForInput(data.ngay_thi_converted),
            thoi_gian_tu: formatTimeForInput(data.thoi_gian_tu_converted),
            thoi_gian_den: formatTimeForInput(data.thoi_gian_den_converted),
          };

          console.log("Formatted Data:", formattedData);
          reset(formattedData);
        }
      } catch (error) {
        NotifyHelper.Error(translate("Error.LoadDataFailed"));
      }
    }
  };

  useEffect(() => {
    loadDetail();
  }, [id]);

  const handleTimeChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    field: "thoi_gian_tu" | "thoi_gian_den"
  ) => {
    const timeValue = e.target.value;
    setValue(field, timeValue);
    setValue(`${field}_converted`, timeValue);
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const dateValue = e.target.value;
    setValue("ngay_thi", dateValue);
    setValue("ngay_thi_converted", moment(dateValue).format("YYYY-MM-DD"));
  };

  const onSubmit = async (data: KyThiItemRequest) => {
    if (!data.ngay_thi) {
      NotifyHelper.Error(translate("Vui lòng chọn ngày thi!"));
      return;
    }

    if (!data.thoi_gian_tu || !data.thoi_gian_den) {
      NotifyHelper.Error(
        translate("Vui lòng chọn thời gian bắt đầu và kết thúc!")
      );
      return;
    }

    const startTime = moment(data.thoi_gian_tu, "HH:mm");
    const endTime = moment(data.thoi_gian_den, "HH:mm");

    if (startTime.isSameOrAfter(endTime)) {
      NotifyHelper.Error(translate("Thời gian kết thúc phải lớn hơn bắt đầu!"));
      return;
    }

    if (!data.dm_truong_id || !data.dm_he_id || !data.dm_khoi_id) {
      NotifyHelper.Error(
        translate("Vui lòng chọn đủ trường/khối/hệ cho kỳ thi!")
      );
      return;
    }

    // Prepare data in format expected by backend
    const formattedData: KyThiItemRequest = {
      ...data,
      nam_hoc,
      ngay_thi_converted: moment(data.ngay_thi).format("YYYY-MM-DD"),
      thoi_gian_tu_converted: data.thoi_gian_tu,
      thoi_gian_den_converted: data.thoi_gian_den,
    };

    setIsSaving(true);
    try {
      const api = id > 0 ? kyThiApi.update : kyThiApi.insert;
      const res = await api(formattedData);

      if (res.is_success) {
        NotifyHelper.Success(
          translate(id > 0 ? "Success.Update" : "Success.Create")
        );
        onSuccess();
      } else {
        NotifyHelper.Error(res.message || translate("Error.Generic"));
      }
    } catch (error) {
      NotifyHelper.Error(translate("Error.Generic"));
    }
    setIsSaving(false);
  };

  const handleComboboxChange =
    (field: keyof KyThiItemRequest) => (value: number) => {
      setValue(field, value);
    };

  const renderValidationError = (error: boolean, message: string) => {
    if (!error) return null;
    return (
      <Text
        as="span"
        sx={{ color: "danger.fg", fontSize: 1, mt: 1, display: "block" }}
      >
        {message}
      </Text>
    );
  };

  return (
    <Dialog
      title={translate(id > 0 ? "Base.Label.Update" : "Base.Label.Create")}
      onClose={onCancel}
      width="large"
    >
      <Box as="form" onSubmit={handleSubmit(onSubmit)} sx={{ p: 0 }}>
        <FormControl sx={{ mb: 3 }}>
          <FormControl.Label>{translate("Trường")}</FormControl.Label>
          <ComboboxTruong
            value={formValues.dm_truong_id}
            onValueChanged={handleComboboxChange("dm_truong_id")}
          />
        </FormControl>

        <FormControl sx={{ mb: 3 }}>
          <FormControl.Label>{translate("Khối")}</FormControl.Label>
          <ComboboxKhoi
            value={formValues.dm_khoi_id}
            dm_truong_id={formValues.dm_truong_id}
            onValueChanged={handleComboboxChange("dm_khoi_id")}
          />
        </FormControl>

        <FormControl sx={{ mb: 3 }}>
          <FormControl.Label>{translate("Hệ")}</FormControl.Label>
          <ComboboxHe
            value={formValues.dm_he_id}
            onValueChanged={handleComboboxChange("dm_he_id")}
          />
        </FormControl>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 3, mb: 3 }}>
          <FormControl required>
            <FormControl.Label>{translate("Kỳ thi")}</FormControl.Label>
            <TextInput
              {...register("ten_ky_thi", { required: true })}
              block
              aria-invalid={errors.ten_ky_thi ? true : undefined}
              sx={
                errors.ten_ky_thi
                  ? {
                      borderColor: "danger.emphasis",
                      "&:focus-within": { borderColor: "danger.emphasis" },
                    }
                  : undefined
              }
            />
            {renderValidationError(
              !!errors.ten_ky_thi,
              translate("Vui lòng điền tên kỳ thi")
            )}
          </FormControl>

          <FormControl required>
            <FormControl.Label>
              {translate("Kỳ thi (English)")}
            </FormControl.Label>
            <TextInput
              {...register("ten_ky_thi_en", { required: true })}
              block
              aria-invalid={errors.ten_ky_thi_en ? true : undefined}
              sx={
                errors.ten_ky_thi_en
                  ? {
                      borderColor: "danger.emphasis",
                      "&:focus-within": { borderColor: "danger.emphasis" },
                    }
                  : undefined
              }
            />
            {renderValidationError(
              !!errors.ten_ky_thi_en,
              translate("Vui lòng điền tên kỳ thi (English)")
            )}
          </FormControl>
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            gap: 3,
            mb: 3,
          }}
        >
          <FormControl>
            <FormControl.Label>{translate("Ngày bắt đầu")}</FormControl.Label>
            <TextInput
              type="date"
              value={formValues.ngay_thi || ""}
              onChange={handleDateChange}
              block
            />
          </FormControl>

          <FormControl>
            <FormControl.Label>{translate("Bắt đầu")}</FormControl.Label>
            <TextInput
              type="time"
              value={formValues.thoi_gian_tu || ""}
              onChange={(e) => handleTimeChange(e, "thoi_gian_tu")}
              block
            />
          </FormControl>

          <FormControl>
            <FormControl.Label>{translate("Kết thúc")}</FormControl.Label>
            <TextInput
              type="time"
              value={formValues.thoi_gian_den || ""}
              onChange={(e) => handleTimeChange(e, "thoi_gian_den")}
              block
            />
          </FormControl>
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3, mb: 3 }}>
          <FormControl>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <input
                type="checkbox"
                id="da-ket-thuc-checkbox"
                checked={formValues.da_ket_thuc}
                onChange={(e) => setValue("da_ket_thuc", e.target.checked)}
              />
              <FormControl.Label htmlFor="da-ket-thuc-checkbox" sx={{ mb: 0 }}>
                {translate("Đã kết thúc")}
              </FormControl.Label>
            </Box>
          </FormControl>
        </Box>
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
          <ButtonGroup>
            <Button onClick={onCancel}>{translate("Base.Label.Cancel")}</Button>
            <Button variant="primary" type="submit" disabled={isSaving}>
              {translate(id > 0 ? "Base.Label.Update" : "Base.Label.Create")}
            </Button>
          </ButtonGroup>
        </Box>
      </Box>
    </Dialog>
  );
};

export default KyThiDetailModal;
